import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X, Crosshair, Camera, Minus, Plus, MagnifyingGlass, CheckCircle, QrCode,
} from '@phosphor-icons/react';
import { apiFormaleta, medidasDe } from '@/api/formaleta';
import type {
  AltaResultado, CatalogosFormaleta, ModeloFormaleta, RenglonFormaleta,
} from '@/api/formaleta';
import { Aviso, Chip, Foto, Pastilla, SinRegistrar } from './comunes';
import { Croquis } from './Croquis';

// INVENTARIAR — la segunda función de la pantalla.
//
// Y no es «registré un lote que llegó de fábrica». Daniel lo describió así:
// «hay muchas que se han fabricado y nunca se han inventariado […] las que
// tienen un poco de documentación a nivel de base de datos son las que se han
// encontrado en el momento». O sea: es un flujo de HALLAZGO. Por eso el
// formulario pregunta DÓNDE la encontraste y CUÁNTAS iguales hay ahí, y por eso
// la foto va primero.
//
// Dos caminos:
//   · un modelo que YA existe como renglón  → se eligen modelo y renglón
//   · un modelo nuevo                       → medidas + categoría, y el
//     `Nombre` lo autogenera el backend con la convención viva
//     (`<ABREV>-<ANCHO>X<LARGO>X<FONDO>`). El operario no escribe
//     identificadores: escribe medidas y toca una categoría.

type Modo = 'existente' | 'nuevo';

export function PanelAlta({ catalogos, inicial, onCerrar }: {
  catalogos: CatalogosFormaleta;
  /** De dónde se abrió: una tarjeta de modelo/renglón, o el botón suelto. */
  inicial?: { modelo: ModeloFormaleta; idForm?: number } | null;
  onCerrar: () => void;
}) {
  const qc = useQueryClient();

  // Arranca SIEMPRE en «existente», incluso sin preselección, y es una lectura
  // de los datos: hay 509 renglones con 18.961 piezas declaradas y 0
  // inventariadas una por una. Lo abrumadoramente común es encontrar una pieza
  // de un modelo que ya existe; el modelo nuevo es el caso de nuestra planta.
  const [modo, setModo] = useState<Modo>('existente');
  const [modelo, setModelo] = useState<ModeloFormaleta | null>(inicial?.modelo ?? null);
  const [idForm, setIdForm] = useState<number | null>(inicial?.idForm ?? null);

  // Modelo nuevo
  const [idCategoria, setIdCategoria] = useState<number | null>(null);
  const [ancho, setAncho] = useState('');
  const [largo, setLargo] = useState('');
  const [fondo, setFondo] = useState('');

  // Comunes
  const [cuantas, setCuantas] = useState(1);
  const [ubicacion, setUbicacion] = useState('');
  const [obra, setObra] = useState('');
  const [idEstado, setIdEstado] = useState<number | null>(
    catalogos.estados.find(e => e.disponible)?.id ?? null);
  const [marcada, setMarcada] = useState(false);
  const [geo, setGeo] = useState<{ lat: number; lng: number; precision: number } | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [foto, setFoto] = useState<File | null>(null);
  const [resultado, setResultado] = useState<AltaResultado | null>(null);
  const [avisoFoto, setAvisoFoto] = useState<string | null>(null);

  const inputFoto = useRef<HTMLInputElement>(null);
  const urlPrevia = useMemo(() => (foto ? URL.createObjectURL(foto) : null), [foto]);
  useEffect(() => () => { if (urlPrevia) URL.revokeObjectURL(urlPrevia); }, [urlPrevia]);

  const cat = catalogos.categorias.find(c => c.id === idCategoria) ?? null;

  const nAncho = numero(ancho);
  const nLargo = numero(largo);
  const nFondo = numero(fondo);

  // Vista previa del nombre. ⚠️ Es una RÉPLICA de ArmarNombre del backend, que
  // es el que manda: acá solo sirve para que el operario vea qué va a quedar
  // antes de guardar. Si las dos divergen, la que vale es la del servidor.
  const nombrePrevio = cat?.abreviatura && nAncho !== null && nLargo !== null
    ? `${cat.abreviatura.toUpperCase()}-${fmt(nAncho)}X${fmt(nLargo)}${nFondo !== null ? `X${fmt(nFondo)}` : ''}`
    : null;

  const alta = useMutation({
    mutationFn: () => apiFormaleta.inventariar({
      idForm: modo === 'existente' ? idForm : null,
      nuevoModelo: modo === 'nuevo'
        ? { idCategoria, ancho: nAncho, largo: nLargo, fondo: nFondo, unidad: 'EA' }
        : null,
      cuantas,
      ubicacion: ubicacion.trim() || null,
      idObra: obra.trim() || null,
      idEstado,
      latitud: geo?.lat ?? null,
      longitud: geo?.lng ?? null,
      marcadaAhora: marcada,
    }),
    onSuccess: async (r) => {
      setResultado(r);
      // La foto se sube DESPUÉS del alta porque hasta acá no existe a qué
      // colgarla. Va al RENGLÓN y no a la pieza cuando el modelo todavía no
      // tiene foto: eso arregla el hueco de verdad (0 fotos en 509 renglones) y
      // sirve para todas las piezas del modelo, no solo para la que se acaba de
      // encontrar.
      if (foto) {
        try {
          // Al RENGLÓN mientras el modelo no tenga foto; a la pieza cuando ya
          // la tiene (ahí la foto nueva es de ESA pieza: un golpe, cómo se
          // encontró). Sin piezas devueltas no hay a qué colgarla, así que
          // también va al renglón.
          const alRenglon = !modelo?.urlFoto || r.piezas.length === 0;
          const res = alRenglon
            ? await apiFormaleta.fotoRenglon(r.idForm, foto)
            : await apiFormaleta.fotoPieza(r.piezas[0]!.id, foto);
          setAvisoFoto(res.advertencia);
        } catch (e) {
          // La pieza YA quedó inventariada: una foto que no subió no puede
          // leerse como que el alta falló.
          setAvisoFoto(`La pieza se guardó, pero la foto no subió: ${(e as Error).message}`);
        }
      }
      qc.invalidateQueries({ queryKey: ['formaleta'] });
    },
  });

  const puedeGuardar = modo === 'existente'
    ? idForm !== null
    : idCategoria !== null && nAncho !== null && nLargo !== null;

  function pedirGps() {
    setGeoError(null);
    if (!navigator.geolocation) {
      setGeoError('Este dispositivo no da ubicación.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      p => setGeo({
        lat: +p.coords.latitude.toFixed(6),
        lng: +p.coords.longitude.toFixed(6),
        precision: Math.round(p.coords.accuracy),
      }),
      e => setGeoError(e.message || 'No se pudo leer la ubicación.'),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  function reiniciar() {
    setResultado(null);
    setFoto(null);
    setAvisoFoto(null);
    setCuantas(1);
    alta.reset();
  }

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center">
      <div className="flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-t-xl bg-white sm:rounded-xl">
        <header className="flex shrink-0 items-center gap-2 border-b border-brand-gray-100 px-4 py-3">
          <QrCode size={18} weight="bold" className="text-brand-gray-500" />
          <h2 className="flex-1 text-base font-semibold text-brand-gray-500">
            {resultado ? 'Piezas inventariadas' : 'Inventariar una pieza'}
          </h2>
          <button type="button" onClick={onCerrar}
            className="rounded p-2 text-brand-gray-400 hover:bg-brand-bg">
            <X size={18} weight="bold" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4">
          {resultado
            ? <Exito resultado={resultado} avisoFoto={avisoFoto} onOtra={reiniciar} onCerrar={onCerrar} />
            : (
              <>
                {!catalogos.piezasHabilitadas && (
                  <div className="mb-4">
                    <Aviso
                      tono="alerta"
                      titulo="El alta todavía no se puede guardar"
                      detalle={catalogos.motivoDeshabilitado}
                      pista="El formulario se puede llenar y revisar, pero el botón de guardar va a devolver este mismo motivo hasta que la migración corra."
                    />
                  </div>
                )}

                {/* ── Qué pieza es ── */}
                <div className="mb-4 flex gap-2">
                  <Chip texto="Un modelo que ya existe" activo={modo === 'existente'}
                    onClick={() => setModo('existente')} />
                  <Chip texto="Un modelo nuevo" activo={modo === 'nuevo'}
                    onClick={() => setModo('nuevo')} />
                </div>

                {modo === 'existente'
                  ? <ElegirExistente
                      modelo={modelo} idForm={idForm}
                      onModelo={m => { setModelo(m); setIdForm(null); }}
                      onRenglon={setIdForm}
                    />
                  : <ModeloNuevo
                      categorias={catalogos.categorias}
                      idCategoria={idCategoria} onCategoria={setIdCategoria}
                      ancho={ancho} largo={largo} fondo={fondo}
                      onAncho={setAncho} onLargo={setLargo} onFondo={setFondo}
                      nombrePrevio={nombrePrevio}
                      colorHex={cat?.colorHex ?? null}
                    />}

                {/* ── Cuántas iguales hay ahí ── */}
                <Campo etiqueta="¿Cuántas iguales hay ahí?"
                  ayuda="Cada una emite su propio consecutivo y su propio QR. Tope 200: después hay que marcarlas a mano.">
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setCuantas(n => Math.max(1, n - 1))}
                      className="rounded-lg border border-brand-gray-200 p-3 text-brand-gray-500 hover:bg-brand-bg">
                      <Minus size={16} weight="bold" />
                    </button>
                    <input
                      type="number" inputMode="numeric" min={1} max={200} value={cuantas}
                      onChange={e => setCuantas(Math.min(200, Math.max(1, Number(e.target.value) || 1)))}
                      className="w-20 rounded-lg border border-brand-gray-200 py-3 text-center font-mono text-xl font-bold text-brand-gray-500"
                    />
                    <button type="button" onClick={() => setCuantas(n => Math.min(200, n + 1))}
                      className="rounded-lg border border-brand-gray-200 p-3 text-brand-gray-500 hover:bg-brand-bg">
                      <Plus size={16} weight="bold" />
                    </button>
                  </div>
                </Campo>

                {/* ── Dónde la encontraste ── */}
                <Campo etiqueta="¿Dónde la encontraste?"
                  ayuda="La bodega, el patio, el sector. Es el paradero de la pieza, y hoy no hay ninguno registrado.">
                  <input
                    value={ubicacion} onChange={e => setUbicacion(e.target.value)}
                    placeholder="Bodega central, patio norte…"
                    className="w-full rounded-lg border border-brand-gray-200 px-3 py-3 text-sm text-brand-gray-500 placeholder:text-brand-gray-300"
                  />
                </Campo>

                <Campo etiqueta="Obra (opcional)"
                  ayuda="El código que reconoce la cuadrilla (VN-M.14). Calza por texto con ObrasAD.">
                  <SelectorObra valor={obra} onCambio={setObra} />
                </Campo>

                {/* ── GPS ── */}
                <Campo etiqueta="Coordenada"
                  ayuda="De las 353 tablas de la base, la única con latitud/longitud era `fotos`: ni las obras ni las bodegas tienen coordenadas. El GPS empieza a existir con las piezas que se registren desde ahora.">
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={pedirGps}
                      className="flex items-center gap-1.5 rounded-lg border border-brand-gray-200 px-3 py-3 text-sm font-semibold text-brand-gray-500 hover:bg-brand-bg">
                      <Crosshair size={16} weight="bold" /> Usar mi ubicación
                    </button>
                    {geo
                      ? <span className="font-mono text-[11px] text-brand-gray-400">
                          {geo.lat}, {geo.lng} <span className="text-brand-gray-300">±{geo.precision} m</span>
                        </span>
                      : <SinRegistrar que="sin coordenada" />}
                  </div>
                  {geoError && <p className="mt-1 text-[11px] text-brand-red-200">{geoError}</p>}
                </Campo>

                {/* ── Estado ── */}
                {catalogos.estados.length > 0 ? (
                  <Campo etiqueta="Estado">
                    <div className="flex flex-wrap gap-2">
                      {catalogos.estados.map(e => (
                        <Chip key={e.id} texto={e.estado} colorHex={e.colorHex}
                          activo={idEstado === e.id} onClick={() => setIdEstado(e.id)} />
                      ))}
                    </div>
                  </Campo>
                ) : (
                  <Campo etiqueta="Estado">
                    <SinRegistrar
                      que="el catálogo de estados todavía no existe"
                      detalle={catalogos.motivoDeshabilitado || undefined}
                    />
                  </Campo>
                )}

                {/* ── Marca física ── */}
                <Campo etiqueta="Marca física"
                  ayuda="Marcarla es un hecho aparte de inventariarla: una pieza puede entrar hoy a la base y recibir su marca una semana después.">
                  <label className="flex items-center gap-2 text-sm text-brand-gray-500">
                    <input type="checkbox" checked={marcada}
                      onChange={e => setMarcada(e.target.checked)}
                      className="h-5 w-5 accent-brand-green-200" />
                    Ya le pegué la marca
                  </label>
                </Campo>

                {/* ── Foto ── */}
                <Campo etiqueta="Foto"
                  ayuda="Va primero en la tarjeta de campo: el código del fabricante no le dice nada a un operario nuevo. Hoy no hay ni una en los 509 renglones.">
                  <button type="button" onClick={() => inputFoto.current?.click()}
                    className="flex items-center gap-3 rounded-lg border border-brand-gray-200 p-2 hover:bg-brand-bg">
                    <Foto url={urlPrevia} alt="foto de la pieza" lado={64} />
                    <span className="flex items-center gap-1.5 pr-2 text-sm font-semibold text-brand-gray-500">
                      <Camera size={16} weight="bold" />
                      {foto ? 'Cambiar la foto' : 'Tomar una foto'}
                    </span>
                  </button>
                  <input ref={inputFoto} type="file" accept="image/*" capture="environment" hidden
                    onChange={e => { setFoto(e.target.files?.[0] ?? null); e.target.value = ''; }} />
                </Campo>

                {alta.isError && (
                  <div className="mb-3">
                    <Aviso tono="alerta" titulo="No se pudo inventariar"
                      detalle={(alta.error as Error).message} />
                  </div>
                )}
              </>
            )}
        </div>

        {!resultado && (
          <footer className="shrink-0 border-t border-brand-gray-100 p-3">
            <button
              type="button"
              disabled={!puedeGuardar || alta.isPending}
              onClick={() => alta.mutate()}
              className="w-full rounded-lg bg-brand-green-100 py-4 text-base font-bold text-black hover:bg-brand-green-200 disabled:bg-brand-gray-200 disabled:text-brand-gray-400"
            >
              {alta.isPending
                ? 'Guardando…'
                : `Inventariar ${cuantas} ${cuantas === 1 ? 'pieza' : 'piezas'}`}
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}

// ── Elegir un modelo que ya existe ──────────────────────────────────────────

function ElegirExistente({ modelo, idForm, onModelo, onRenglon }: {
  modelo: ModeloFormaleta | null;
  idForm: number | null;
  onModelo: (m: ModeloFormaleta) => void;
  onRenglon: (id: number) => void;
}) {
  const [q, setQ] = useState('');

  const { data: encontrados } = useQuery({
    queryKey: ['formaleta', 'modelos', 'alta', q],
    queryFn: () => apiFormaleta.modelos({ q }),
    enabled: !modelo && q.trim().length >= 2,
  });

  const { data: detalle } = useQuery({
    queryKey: ['formaleta', 'modelo', modelo?.nombre],
    queryFn: () => apiFormaleta.modelo(modelo!.nombre),
    enabled: !!modelo,
  });

  // Con un solo renglón no se pregunta: elegir entre uno es una fricción
  // gratuita en una pantalla que se usa con guante.
  useEffect(() => {
    const rs = detalle?.renglones;
    if (rs && rs.length === 1 && idForm === null) onRenglon(rs[0]!.idForm);
  }, [detalle, idForm, onRenglon]);

  if (!modelo) {
    return (
      <Campo etiqueta="¿Qué pieza es?" ayuda="Buscá por código, categoría o medida.">
        <div className="flex items-center gap-2 rounded-lg border border-brand-gray-200 px-3">
          <MagnifyingGlass size={16} weight="bold" className="text-brand-gray-300" />
          <input
            value={q} onChange={e => setQ(e.target.value)} autoFocus
            placeholder="PARAPET, CR-90X120, pretil…"
            className="flex-1 py-3 text-sm text-brand-gray-500 placeholder:text-brand-gray-300 focus:outline-none"
          />
        </div>
        <div className="mt-2 max-h-64 space-y-1.5 overflow-y-auto">
          {(encontrados || []).slice(0, 12).map(m => (
            <button key={m.nombre} type="button" onClick={() => onModelo(m)}
              className="flex w-full items-center gap-2 rounded-lg border border-brand-gray-200 p-2 text-left hover:bg-brand-bg">
              {m.abreviatura
                ? <Pastilla texto={m.abreviatura} colorHex={m.colorHex} />
                : <SinRegistrar que="sin cat." />}
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-bold text-brand-gray-500">
                  {medidasDe(m) || 'sin medidas'}
                </span>
                <span className="block truncate font-mono text-[10px] text-brand-gray-400">
                  {m.nombre}
                </span>
              </span>
              <span className="shrink-0 font-mono text-[11px] text-brand-gray-400">
                {m.piezasDeclaradas}
              </span>
            </button>
          ))}
          {q.trim().length >= 2 && encontrados && encontrados.length === 0 && (
            <p className="py-2 text-xs text-brand-gray-400">
              Nada con ese texto. Si la pieza es de nuestra planta, probablemente
              todavía no existe como modelo: usá «Un modelo nuevo».
            </p>
          )}
        </div>
      </Campo>
    );
  }

  return (
    <Campo etiqueta="¿De qué renglón es?"
      ayuda="Un mismo modelo puede venir de varios pedidos. El renglón es la trazabilidad con el fabricante.">
      <div className="mb-2 flex items-center gap-2 rounded-lg bg-brand-bg p-2">
        {modelo.abreviatura && <Pastilla texto={modelo.abreviatura} colorHex={modelo.colorHex} />}
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-bold text-brand-gray-500">
            {medidasDe(modelo) || 'sin medidas'}
          </span>
          <span className="block truncate font-mono text-[10px] text-brand-gray-400">
            {modelo.nombre}
          </span>
        </span>
      </div>
      <div className="space-y-1.5">
        {(detalle?.renglones || []).map((r: RenglonFormaleta) => (
          <button key={r.idForm} type="button" onClick={() => onRenglon(r.idForm)}
            className={`flex w-full items-center gap-2 rounded-lg border p-2.5 text-left ${
              idForm === r.idForm
                ? 'border-brand-gray-500 bg-brand-gray-500/5'
                : 'border-brand-gray-200 hover:bg-brand-bg'
            }`}>
            {idForm === r.idForm && (
              <CheckCircle size={16} weight="fill" className="shrink-0 text-brand-green-200" />
            )}
            <span className="min-w-0 flex-1 text-xs font-semibold text-brand-gray-500">
              {r.pedido || 'sin pedido'}
              <span className="ml-2 font-mono text-[10px] font-normal text-brand-gray-400">
                #{r.idForm}
              </span>
            </span>
            <span className="shrink-0 font-mono text-[11px] text-brand-gray-400">
              {r.piezasInventariadas} / {r.piezasDeclaradas}
            </span>
          </button>
        ))}
      </div>
    </Campo>
  );
}

// ── Un modelo nuevo ─────────────────────────────────────────────────────────

function ModeloNuevo({
  categorias, idCategoria, onCategoria,
  ancho, largo, fondo, onAncho, onLargo, onFondo,
  nombrePrevio, colorHex,
}: {
  categorias: CatalogosFormaleta['categorias'];
  idCategoria: number | null;
  onCategoria: (id: number) => void;
  ancho: string; largo: string; fondo: string;
  onAncho: (v: string) => void;
  onLargo: (v: string) => void;
  onFondo: (v: string) => void;
  nombrePrevio: string | null;
  colorHex: string | null;
}) {
  return (
    <>
      <Campo etiqueta="Categoría"
        ayuda="La abreviatura de la categoría ES el prefijo del nombre: sin ella no hay nombre que armar.">
        <div className="flex flex-wrap gap-2">
          {categorias.map(c => (
            <Chip
              key={c.id}
              texto={`${c.abreviatura || '???'} · ${c.nombre || 'sin nombre'}`}
              colorHex={c.colorHex}
              activo={idCategoria === c.id}
              onClick={() => onCategoria(c.id)}
            />
          ))}
        </div>
      </Campo>

      <Campo etiqueta="Medidas en centímetros">
        <div className="flex items-start gap-4">
          <div className="flex-1 space-y-2">
            <Medida etiqueta="Ancho" valor={ancho} onCambio={onAncho} />
            <Medida etiqueta="Largo" valor={largo} onCambio={onLargo} />
            <Medida etiqueta="Fondo" valor={fondo} onCambio={onFondo} opcional />
          </div>
          <Croquis
            ancho={numero(ancho)} largo={numero(largo)} fondo={numero(fondo)}
            color={colorHex}
          />
        </div>
      </Campo>

      <Campo etiqueta="Nombre que va a quedar"
        ayuda="Lo arma el servidor con la convención que ya usa la app (CAI-40X80X5). Nadie teclea identificadores a mano.">
        {nombrePrevio
          ? <p className="rounded-lg bg-brand-bg px-3 py-3 font-mono text-base font-bold text-brand-gray-500">
              {nombrePrevio}
            </p>
          : <SinRegistrar que="faltan la categoría y las medidas" />}
        <p className="mt-1.5 text-[11px] text-brand-gray-400">
          Entra como <span className="font-mono font-semibold">AD01</span> ·
          fabricante <span className="font-semibold">Adelante</span>. Si ese
          nombre ya existe, la pieza se cuelga de ese renglón en vez de duplicar
          el modelo.
        </p>
      </Campo>
    </>
  );
}

// ── Éxito: los consecutivos emitidos ────────────────────────────────────────

function Exito({ resultado, avisoFoto, onOtra, onCerrar }: {
  resultado: AltaResultado;
  avisoFoto: string | null;
  onOtra: () => void;
  onCerrar: () => void;
}) {
  return (
    <div>
      <div className="mb-4 flex items-center gap-2 text-brand-green-200">
        <CheckCircle size={22} weight="fill" />
        <p className="text-sm font-bold text-brand-gray-500">
          {resultado.piezas.length} {resultado.piezas.length === 1 ? 'pieza' : 'piezas'} en el inventario
        </p>
      </div>

      <p className="mb-1 text-xs text-brand-gray-400">
        {resultado.renglonCreado && 'Se creó el modelo '}
        {resultado.renglonReusado && 'El modelo ya existía, se usó ese renglón: '}
        <span className="font-mono font-semibold text-brand-gray-500">{resultado.nombre}</span>
      </p>

      <p className="mb-2 mt-4 text-xs font-bold uppercase tracking-wide text-brand-gray-400">
        Consecutivos emitidos — hay que marcarlos en el aluminio
      </p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {resultado.piezas.map(p => (
          <span key={p.id}
            className="rounded-lg border border-brand-gray-200 bg-brand-bg py-3 text-center font-mono text-base font-bold text-brand-gray-500">
            {p.consecutivo}
          </span>
        ))}
      </div>

      {avisoFoto && (
        <p className="mt-3 text-[11px] font-semibold text-brand-red-200">{avisoFoto}</p>
      )}

      <div className="mt-5 flex gap-2">
        <button type="button" onClick={onOtra}
          className="flex-1 rounded-lg bg-brand-green-100 py-3.5 text-sm font-bold text-black hover:bg-brand-green-200">
          Inventariar otra
        </button>
        <button type="button" onClick={onCerrar}
          className="rounded-lg border border-brand-gray-200 px-5 py-3.5 text-sm font-semibold text-brand-gray-500 hover:bg-brand-bg">
          Cerrar
        </button>
      </div>
    </div>
  );
}

// ── Piezas chicas del formulario ────────────────────────────────────────────

function Campo({ etiqueta, ayuda, children }: {
  etiqueta: string;
  ayuda?: string;
  children: ReactNode;
}) {
  return (
    <div className="mb-5">
      <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-brand-gray-400">
        {etiqueta}
      </p>
      {children}
      {ayuda && <p className="mt-1.5 text-[11px] leading-snug text-brand-gray-400">{ayuda}</p>}
    </div>
  );
}

function Medida({ etiqueta, valor, onCambio, opcional }: {
  etiqueta: string;
  valor: string;
  onCambio: (v: string) => void;
  opcional?: boolean;
}) {
  return (
    <label className="flex items-center gap-2">
      <span className="w-12 shrink-0 text-xs font-semibold text-brand-gray-500">{etiqueta}</span>
      <input
        type="number" inputMode="decimal" step="0.1" min="0"
        value={valor} onChange={e => onCambio(e.target.value)}
        placeholder={opcional ? 'opcional' : '0'}
        className="w-full rounded-lg border border-brand-gray-200 px-3 py-2.5 font-mono text-base text-brand-gray-500 placeholder:font-sans placeholder:text-xs placeholder:text-brand-gray-300"
      />
    </label>
  );
}

/** Los códigos de obra activos, como sugerencia y no como campo cerrado. */
function SelectorObra({ valor, onCambio }: { valor: string; onCambio: (v: string) => void }) {
  const { data } = useQuery({
    queryKey: ['formaleta', 'obras'],
    queryFn: () => apiFormaleta.obras(),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <>
      <input
        list="formaleta-obras" value={valor} onChange={e => onCambio(e.target.value)}
        placeholder="VN-M.14"
        className="w-full rounded-lg border border-brand-gray-200 px-3 py-3 font-mono text-sm text-brand-gray-500 placeholder:font-sans placeholder:text-brand-gray-300"
      />
      <datalist id="formaleta-obras">
        {(data || []).map(o => <option key={o.obra} value={o.obra} />)}
      </datalist>
    </>
  );
}

function numero(v: string): number | null {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** `40` y no `40.00`, igual que el `Medida()` del backend. */
function fmt(n: number): string {
  return String(Number(n));
}
